import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Eye, Search, Filter, User, Package, Calendar, ArrowRight, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import axios from "axios";
import AssignmentModal from "./AssignmentModal";
import EquipmentDetailsModal from "./EquipmentDetailsModal";
import ConfirmationModal from "./ConfirmationModal";
import InspectionModal from "./InspectionModal";

const EquipmentAssignments = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('assignments');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('assigned');
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState('all');
  const [validityFilter, setValidityFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [overviewSortBy, setOverviewSortBy] = useState('');
  const [overviewSortOrder, setOverviewSortOrder] = useState('asc');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEquipmentDetails, setSelectedEquipmentDetails] = useState(null);
  const [showReturnConfirmation, setShowReturnConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [assignmentToReturn, setAssignmentToReturn] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [selectedEquipmentForInspection, setSelectedEquipmentForInspection] = useState(null);
  const [multiInspectionMode, setMultiInspectionMode] = useState(false);
  const [multiInspectionQueue, setMultiInspectionQueue] = useState([]);
  const [currentInspectionIndex, setCurrentInspectionIndex] = useState(0);

  const [assignmentData, setAssignmentData] = useState({
    assignments: [],
    users: [],
    safetyEquipment: [],
    safetyEquipmentTypes: []
  });

  // Format days status for validity date (helper function)
  const formatDaysStatus = (validityDate) => {
    if (!validityDate) return null;
    
    const today = new Date();
    const expiry = new Date(validityDate);
    const timeDiff = expiry.getTime() - today.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    
    if (daysDiff > 0) return `Expires in ${daysDiff} days`;
    if (daysDiff < 0) return `Expired ${Math.abs(daysDiff)} days ago`;
    return 'Expires today';
  };

  // API Base URL
  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

  // Fetch assignment data
  useEffect(() => {
    fetchAssignmentData();
  }, []);

  // Refetch data when status filter changes
  useEffect(() => {
    fetchAssignmentData(statusFilter);
  }, [statusFilter]);

  const fetchAssignmentData = async (status = statusFilter) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Build query parameters for assignments endpoint
      const assignmentParams = new URLSearchParams();
      if (status && status !== 'all') {
        assignmentParams.append('status', status);
      }
      const assignmentUrl = `${API_BASE_URL}/equipmentassignment/${assignmentParams.toString() ? '?' + assignmentParams.toString() : ''}`;

      const [assignmentsResponse, usersResponse, safetyEquipmentResponse, safetyEquipmentTypesResponse] = await Promise.all([
        axios.get(assignmentUrl, config),
        axios.get(`${API_BASE_URL}/users/`, config),
        axios.get(`${API_BASE_URL}/safetyequipment/`, config),
        axios.get(`${API_BASE_URL}/safetyequipmenttype/?scope=company`, config)
      ]);

      setAssignmentData({
        assignments: assignmentsResponse.data,
        users: usersResponse.data,
        safetyEquipment: safetyEquipmentResponse.data,
        safetyEquipmentTypes: safetyEquipmentTypesResponse.data
      });

      console.log('📌 Assignment Data:', assignmentsResponse.data);

    } catch (error) {
      console.error('Error fetching assignment data:', error);
      setError(error.response?.data?.detail || 'Failed to fetch assignment data');
    } finally {
      setLoading(false);
    }
  };

  // Get user overview data with next inspection dates for each equipment type
  const getUserOverviewData = () => {
    const userMap = new Map();
    
    // Initialize users with empty equipment object
    assignmentData.users.forEach(user => {
      userMap.set(user.id, {
        id: user.id,
        name: user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username,
        username: user.username,
        equipment: {} // key: equipment_type_id, value: next_inspection_date
      });
    });

    // Process active assignments and map equipment next inspection dates to equipment types
    assignmentData.assignments
      .filter(assignment => assignment.status === 'assigned')
      .forEach(assignment => {
        const userId = assignment.user;
        
        if (userMap.has(userId)) {
          const user = userMap.get(userId);
          
          // Find the equipment to get its type
          const equipment = assignmentData.safetyEquipment.find(eq => eq.id === assignment.safety_equipment);
          
          if (equipment && equipment.safety_equipment_type) {
            const equipmentTypeId = equipment.safety_equipment_type;
            const nextInspectionDate = equipment.safety_equipment_date_of_validity;
            
            if (nextInspectionDate) {
              // If user already has this equipment type, compare dates and keep the earliest one
              if (user.equipment[equipmentTypeId]) {
                const existingDate = new Date(user.equipment[equipmentTypeId]);
                const newDate = new Date(nextInspectionDate);
                if (newDate < existingDate) {
                  user.equipment[equipmentTypeId] = nextInspectionDate;
                }
              } else {
                user.equipment[equipmentTypeId] = nextInspectionDate;
              }
            }
          }
        }
      });

    return Array.from(userMap.values());
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort icon for header
  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  // Get username by user ID
  const getUsernameById = (userId) => {
    const user = assignmentData.users.find(u => u.id === userId);
    return user?.username || `ID: ${userId}`;
  };

  // Filter assignments based on search term and status
  const getFilteredAssignments = () => {
    let filtered = assignmentData.assignments;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(assignment => assignment.status === statusFilter);
    }

    // Filter by equipment type
    if (equipmentTypeFilter !== 'all') {
      filtered = filtered.filter(assignment => assignment.equipment_type === equipmentTypeFilter);
    }

    // Filter by validity period
    if (validityFilter !== 'all') {
      filtered = filtered.filter(assignment => {
        const validityDate = assignment.equipment_next_inspection_date;
        if (!validityDate) {
          return validityFilter === 'no_inspection';
        }
        
        const today = new Date();
        const expiry = new Date(validityDate);
        const timeDiff = expiry.getTime() - today.getTime();
        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        
        switch (validityFilter) {
          case 'valid':
            return daysDiff >= 0;
          case 'expired':
            return daysDiff < 0;
          case 'next30':
            return daysDiff >= 0 && daysDiff <= 30;
          case 'next60':
            return daysDiff >= 0 && daysDiff <= 60;
          case 'next90':
            return daysDiff >= 0 && daysDiff <= 90;
          case 'no_inspection':
            return false; // Already handled above
          default:
            return true;
        }
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(assignment => {
        const username = getUsernameById(assignment.user);
        const equipment = assignmentData.safetyEquipment.find(eq => eq.id === assignment.safety_equipment);
        const serialNumber = equipment?.safety_equipment_serial_number || '';
        
        return assignment.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               username.toLowerCase().includes(searchTerm.toLowerCase()) ||
               assignment.equipment_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               assignment.equipment_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               assignment.assignment_location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               serialNumber.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Sort the filtered data
    if (sortBy) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortBy) {
          case 'user':
            aValue = a.user_name || '';
            bValue = b.user_name || '';
            break;
          case 'equipment':
            aValue = a.equipment_name || '';
            bValue = b.equipment_name || '';
            break;
          case 'assignment_date':
            aValue = new Date(a.assignment_date || 0);
            bValue = new Date(b.assignment_date || 0);
            break;
          case 'next_inspection':
            aValue = new Date(a.equipment_next_inspection_date || 0);
            bValue = new Date(b.equipment_next_inspection_date || 0);
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'location':
            aValue = a.assignment_location || '';
            bValue = b.assignment_location || '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  // Get filtered users for overview tab
  const getFilteredUsers = () => {
    let users = getUserOverviewData()
      .filter(user => !searchTerm || user.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Apply sorting for overview tab
    if (overviewSortBy) {
      users.sort((a, b) => {
        let aValue, bValue;
        
        if (overviewSortBy === 'user') {
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
        } else {
          // Sorting by equipment type (overviewSortBy contains equipment type ID)
          aValue = a.equipment[overviewSortBy] ? new Date(a.equipment[overviewSortBy]) : new Date('9999-12-31');
          bValue = b.equipment[overviewSortBy] ? new Date(b.equipment[overviewSortBy]) : new Date('9999-12-31');
        }
        
        if (aValue < bValue) return overviewSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return overviewSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return users;
  };

  // Pagination logic for assignments
  const filteredAssignments = getFilteredAssignments();
  const filteredUsers = getFilteredUsers();
  
  const getCurrentData = () => {
    return activeTab === 'assignments' ? filteredAssignments : filteredUsers;
  };

  const currentData = getCurrentData();
  const totalPages = Math.ceil(currentData.length / itemsPerPage) || 1;
  const paginatedData = currentData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters change or items per page changes
  useEffect(() => {
    const filteredData = activeTab === 'assignments' ? getFilteredAssignments() : getFilteredUsers();
    const calculatedTotalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [itemsPerPage, activeTab, searchTerm, statusFilter, equipmentTypeFilter, validityFilter, assignmentData, currentPage]);

  // Reset to first page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, statusFilter, equipmentTypeFilter, validityFilter, sortBy, sortOrder]);

  // Clear selections when tab changes or data changes
  useEffect(() => {
    setSelectedItems(new Set());
  }, [activeTab, searchTerm, statusFilter, equipmentTypeFilter, validityFilter]);

  // Handle individual item selection
  const handleItemSelect = (itemId) => {
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(itemId)) {
      newSelectedItems.delete(itemId);
    } else {
      newSelectedItems.add(itemId);
    }
    setSelectedItems(newSelectedItems);
  };

  // Handle select all items
  const handleSelectAll = (checked) => {
    if (checked) {
      const currentPageItems = paginatedData.map(item => item.id);
      const newSelectedItems = new Set([...selectedItems, ...currentPageItems]);
      setSelectedItems(newSelectedItems);
    } else {
      const currentPageItems = new Set(paginatedData.map(item => item.id));
      const newSelectedItems = new Set([...selectedItems].filter(id => !currentPageItems.has(id)));
      setSelectedItems(newSelectedItems);
    }
  };

  // Check if all current page items are selected
  const isAllSelected = () => {
    if (paginatedData.length === 0) return false;
    return paginatedData.every(item => selectedItems.has(item.id));
  };

  // Check if some current page items are selected (for indeterminate state)
  const isSomeSelected = () => {
    return paginatedData.some(item => selectedItems.has(item.id)) && !isAllSelected();
  };

  // Handle inspection modal
  const openInspectionModal = (assignment) => {
    const equipment = assignmentData.safetyEquipment.find(eq => eq.id === assignment.safety_equipment);
    if (!equipment) return;

    // Check if multiple items are selected and this assignment is among them
    const selectedAssignmentItems = paginatedData.filter(item => selectedItems.has(item.id));
    
    if (selectedAssignmentItems.length > 1 && selectedItems.has(assignment.id)) {
      // Multi-equipment inspection mode - get equipment objects for selected assignments
      const equipmentQueue = selectedAssignmentItems.map(selectedAssignment => {
        const eq = assignmentData.safetyEquipment.find(e => e.id === selectedAssignment.safety_equipment);
        return eq;
      }).filter(Boolean);
      
      setMultiInspectionMode(true);
      setMultiInspectionQueue(equipmentQueue);
      setCurrentInspectionIndex(0);
      setSelectedEquipmentForInspection(equipmentQueue[0]);
    } else {
      // Single equipment inspection mode
      setMultiInspectionMode(false);
      setMultiInspectionQueue([]);
      setCurrentInspectionIndex(0);
      setSelectedEquipmentForInspection(equipment);
    }
    
    setShowInspectionModal(true);
  };

  // Handle inspection completion
  const handleInspectionSave = () => {
    if (multiInspectionMode && currentInspectionIndex < multiInspectionQueue.length - 1) {
      // Move to next equipment in queue
      setCurrentInspectionIndex(prev => prev + 1);
      setSelectedEquipmentForInspection(multiInspectionQueue[currentInspectionIndex + 1]);
    } else {
      // Close inspection modal and refresh data
      setShowInspectionModal(false);
      setMultiInspectionMode(false);
      setMultiInspectionQueue([]);
      setCurrentInspectionIndex(0);
      setSelectedEquipmentForInspection(null);
      fetchAssignmentData(statusFilter); // Refresh data
    }
  };

  // Handle inspection skip
  const handleInspectionSkip = () => {
    handleInspectionSave(); // Same logic as save for now
  };

  // Modal handlers
  const openModal = (mode, assignment = null) => {
    setModalMode(mode);
    setSelectedAssignment(assignment);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAssignment(null);
    setModalMode('create');
  };

  const handleSave = (savedAssignment) => {
    fetchAssignmentData(statusFilter); // Refresh data after save
  };

  // Get status statistics
  const getStatusStats = () => {
    const stats = assignmentData.assignments.reduce((acc, assignment) => {
      acc[assignment.status] = (acc[assignment.status] || 0) + 1;
      return acc;
    }, {});
    
    return {
      assigned: stats.assigned || 0,
      returned: stats.returned || 0,
      lost: stats.lost || 0,
      damaged: stats.damaged || 0
    };
  };

  // Get visible page numbers for pagination
  const getVisiblePages = () => {
    const maxVisiblePages = 5;
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'assigned': return 'bg-white text-green-600 font-bold';
      case 'returned': return 'bg-white text-blue-600 font-bold';
      case 'lost': return 'bg-white text-red-600 font-bold';
      case 'damaged': return 'bg-white text-orange-600 font-bold';
      default: return 'bg-white text-gray-600 font-bold';
    }
  };

  // Get validity status color for inspection dates
  const getValidityColor = (validityDate, assignment) => {
    if (!validityDate) return 'bg-gray-100 text-gray-800';

    // Get equipment to check inspection status
    const equipment = assignmentData.safetyEquipment.find(eq => eq.id === assignment.safety_equipment);
    
    const validity = new Date(validityDate);
    const today = new Date();
    const daysUntilExpiry = Math.floor((validity - today) / (1000 * 60 * 60 * 24));

    // Check if latest inspection failed (if we have inspection data)
    if (equipment?.latest_inspection?.status === 'non-ok') {
      return 'bg-red-100 text-red-800'; // Failed inspection
    } else if (daysUntilExpiry < 0) {
      return 'bg-red-100 text-red-800'; // Expired
    } else if (daysUntilExpiry <= 30) {
      return 'bg-orange-100 text-orange-800'; // Expires within 30 days
    } else if (daysUntilExpiry <= 60) {
      return 'bg-yellow-100 text-yellow-800'; // Expires within 60 days
    } else {
      return 'bg-green-100 text-green-800'; // Valid for more than 60 days
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleReturn = (assignment) => {
    // Check if multiple items are selected and this assignment is among them
    const selectedAssignedItems = paginatedData.filter(item => 
      selectedItems.has(item.id) && item.status === 'assigned'
    );
    
    if (selectedAssignedItems.length > 1 && selectedItems.has(assignment.id)) {
      // Multi-assignment return mode
      setAssignmentToReturn(selectedAssignedItems);
      setShowReturnConfirmation(true);
    } else {
      // Single assignment return mode
      setAssignmentToReturn([assignment]);
      setShowReturnConfirmation(true);
    }
  };

  const confirmReturn = async () => {
    if (!assignmentToReturn || assignmentToReturn.length === 0) return;

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Process each assignment in the array
      const returnPromises = assignmentToReturn.map(async (assignment) => {
        // Update assignment status to returned
        await axios.patch(`${API_BASE_URL}/equipmentassignment/${assignment.id}/`, 
          { 
            status: 'returned',
            return_date: new Date().toISOString()
          },
          config
        );

        // Update equipment status to available
        if (assignment.safety_equipment) {
          try {
            await axios.patch(`${API_BASE_URL}/safetyequipment/${assignment.safety_equipment}/`, 
              { safety_equipment_status: 'available' },
              config
            );
          } catch (equipmentError) {
            console.error(`Failed to update equipment status for ${assignment.safety_equipment}:`, equipmentError);
            // Don't fail the whole operation if equipment status update fails
          }
        }
      });

      await Promise.all(returnPromises);
      
      // Clear selections after successful return
      setSelectedItems(new Set());
      
      fetchAssignmentData(statusFilter); // Refresh data
      setShowReturnConfirmation(false);
      setAssignmentToReturn(null);
    } catch (error) {
      console.error('Error returning equipment:', error);
      alert('Failed to return equipment');
      setShowReturnConfirmation(false);
      setAssignmentToReturn(null);
    }
  };

  const handleDelete = (assignment) => {
    setAssignmentToDelete(assignment);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (!assignmentToDelete) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_BASE_URL}/equipmentassignment/${assignmentToDelete.id}/`, {
        headers: { 
          Authorization: `Bearer ${token}`
        }
      });
      fetchAssignmentData(statusFilter); // Refresh data
      setShowDeleteConfirmation(false);
      setAssignmentToDelete(null);
    } catch (error) {
      console.error('Error deleting assignment:', error);
      alert('Failed to delete assignment');
      setShowDeleteConfirmation(false);
      setAssignmentToDelete(null);
    }
  };

  // Handle sorting for overview tab
  const handleOverviewSort = (column) => {
    if (overviewSortBy === column) {
      setOverviewSortOrder(overviewSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setOverviewSortBy(column);
      setOverviewSortOrder('asc');
    }
  };

  // Handle double-click on equipment date cell
  const handleEquipmentCellDoubleClick = (userId, equipmentTypeId) => {
    // Find all assignments for this user and equipment type
    const userAssignments = assignmentData.assignments.filter(
      assignment => assignment.user === userId && assignment.status === 'assigned'
    );
    
    const equipmentOfType = [];
    
    userAssignments.forEach(assignment => {
      const equipment = assignmentData.safetyEquipment.find(
        eq => eq.id === assignment.safety_equipment && eq.safety_equipment_type === equipmentTypeId
      );
      
      if (equipment) {
        const user = assignmentData.users.find(u => u.id === userId);
        const equipmentType = assignmentData.safetyEquipmentTypes.find(t => t.id === equipmentTypeId);
        
        
        equipmentOfType.push({
          assignment,
          equipment,
          user,
          equipmentType
        });
      }
    });
    
    if (equipmentOfType.length > 0) {
      setSelectedEquipmentDetails(equipmentOfType);
      setShowDetailsModal(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  const statusStats = getStatusStats();

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="ml-4 text-2xl font-bold text-[#002D43]">
            Equipment Assignments
          </h1>
          <button
            onClick={() => openModal('create')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>New Assignment</span>
          </button>
        </div>
        
        <div className="p-4 bg-white rounded-2xl flex flex-col" style={{ height: 'calc(100vh - 90px)' }}>

          {/* Tabs */}
          <div className="mb-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('assignments')}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'assignments'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Package className="h-5 w-5" />
                <span>Assignments</span>
                <span className="bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {assignmentData.assignments.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="h-5 w-5" />
                <span>User Overview</span>
                <span className="bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {assignmentData.users.length}
                </span>
              </button>
            </nav>
          </div>

          {/* Search and Filters */}
          {activeTab === 'assignments' && (
            <div className="mb-4 flex space-x-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search assignments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                />
                <Search
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-4 pr-10 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black max-w-[150px] truncate"
              >
                <option value="all">All Status</option>
                <option value="assigned">Assigned</option>
                <option value="returned">Returned</option>
                <option value="lost">Lost</option>
                <option value="damaged">Damaged</option>
              </select>
              <select
                value={equipmentTypeFilter}
                onChange={(e) => setEquipmentTypeFilter(e.target.value)}
                className="pl-4 pr-10 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black max-w-[200px] truncate"
              >
                <option value="all">All Equipment Types</option>
                {assignmentData.safetyEquipmentTypes.map(type => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
              <select
                value={validityFilter}
                onChange={(e) => setValidityFilter(e.target.value)}
                className="pl-4 pr-10 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black max-w-[200px] truncate"
              >
                <option value="all">Any Validity</option>
                <option value="valid">Valid (Not Expired)</option>
                <option value="expired">Expired</option>
                <option value="next30">Expiring in 30 Days</option>
                <option value="next60">Expiring in 60 Days</option>
                <option value="next90">Expiring in 90 Days</option>
                <option value="no_inspection">No Inspection</option>
              </select>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="mb-4 flex justify-between items-center">
              <div className="flex-1 relative max-w-md">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                />
                <Search
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>
            </div>
          )}

          {/* Content based on active tab */}
          <div className="flex-1 flex flex-col min-h-0">
            {activeTab === 'assignments' && (
              <div className="flex-1 flex flex-col min-h-0">
            {paginatedData.length === 0 ? (
              <div className="text-center py-12">
                <User className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No assignments found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by creating a new equipment assignment.'
                  }
                </p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="w-12 px-6 py-3">
                        <input
                          type="checkbox"
                          checked={isAllSelected()}
                          ref={(input) => {
                            if (input) input.indeterminate = isSomeSelected();
                          }}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('user')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>User</span>
                          {getSortIcon('user')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('equipment')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Equipment</span>
                          {getSortIcon('equipment')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('assignment_date')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Assignment Date</span>
                          {getSortIcon('assignment_date')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('next_inspection')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Next Inspection</span>
                          {getSortIcon('next_inspection')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {getSortIcon('status')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('location')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Location</span>
                          {getSortIcon('location')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((assignment) => (
                      <tr key={assignment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(assignment.id)}
                            onChange={() => handleItemSelect(assignment.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <User className="h-6 w-6 text-gray-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {assignment.user_name || 'Unknown User'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {getUsernameById(assignment.user)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.equipment_type || 'Unknown Equipment'}
                          </div>
                          {(() => {
                            const equipment = assignmentData.safetyEquipment.find(eq => eq.id === assignment.safety_equipment);
                            const serialNumber = equipment?.safety_equipment_serial_number;
                            return serialNumber ? (
                              <div className="text-xs text-gray-500">
                                S/N: {serialNumber}
                              </div>
                            ) : null;
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDateTime(assignment.assignment_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {assignment.equipment_next_inspection_date ? (
                            <div className="flex flex-col">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full mb-1 ${getValidityColor(assignment.equipment_next_inspection_date, assignment)}`}>
                                {formatDate(assignment.equipment_next_inspection_date)}
                              </span>
                              <span className={`text-xs ${
                                (() => {
                                  const equipment = assignmentData.safetyEquipment.find(eq => eq.id === assignment.safety_equipment);
                                  const validityDate = new Date(assignment.equipment_next_inspection_date);
                                  const today = new Date();
                                  const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));
                                  
                                  if (equipment?.latest_inspection?.status === 'non-ok') {
                                    return 'text-red-600'; // Failed inspection
                                  } else if (daysUntilExpiry < 0) {
                                    return 'text-red-600'; // Expired
                                  } else if (daysUntilExpiry <= 30) {
                                    return 'text-orange-600'; // Expires in 30 days or less
                                  } else if (daysUntilExpiry <= 60) {
                                    return 'text-yellow-600'; // Expires between 30-60 days
                                  } else {
                                    return 'text-green-600'; // Valid for more than 60 days
                                  }
                                })()
                              }`}>
                                {formatDaysStatus(assignment.equipment_next_inspection_date)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">No inspection</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(assignment.status)}`}>
                            {assignment.status || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {assignment.assignment_location || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right w-32">
                          <div className="flex space-x-2 justify-end">
                            <button
                              onClick={() => openModal('view', assignment)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {assignment.status === 'assigned' && (
                              <button
                                onClick={() => handleReturn(assignment)}
                                className="text-green-600 hover:text-green-900 relative"
                                title={
                                  selectedItems.has(assignment.id) && selectedItems.size > 1
                                    ? `Mark as Returned (${selectedItems.size} selected items)`
                                    : "Mark as Returned"
                                }
                              >
                                <ArrowRight className="h-4 w-4" />
                                {selectedItems.has(assignment.id) && selectedItems.size > 1 && (
                                  <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                    {selectedItems.size}
                                  </span>
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => openInspectionModal(assignment)}
                              className="text-purple-600 hover:text-purple-900 relative"
                              title={
                                selectedItems.has(assignment.id) && selectedItems.size > 1
                                  ? `Create Inspection (${selectedItems.size} selected items)`
                                  : "Create Inspection"
                              }
                            >
                              <Calendar className="h-4 w-4" />
                              {selectedItems.has(assignment.id) && selectedItems.size > 1 && (
                                <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {selectedItems.size}
                                </span>
                              )}
                            </button>
                            <button
                              onClick={() => openModal('edit', assignment)}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="Edit Assignment"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(assignment)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Assignment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
              </div>
            )}

            {activeTab === 'overview' && (
              <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleOverviewSort('user')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>User</span>
                        {overviewSortBy === 'user' ? (
                          overviewSortOrder === 'asc' ? 
                            <ArrowUp className="h-4 w-4" /> : 
                            <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    {assignmentData.safetyEquipmentTypes.map(type => (
                      <th 
                        key={type.id} 
                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleOverviewSort(type.id)}
                      >
                        <div className="flex items-center justify-center space-x-1">
                          <span>{type.name}</span>
                          {overviewSortBy === type.id ? (
                            overviewSortOrder === 'asc' ? 
                              <ArrowUp className="h-4 w-4" /> : 
                              <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedData.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      {assignmentData.safetyEquipmentTypes.map(type => (
                        <td 
                          key={type.id} 
                          className={`px-4 py-4 text-center text-sm text-gray-900 ${
                            user.equipment[type.id] ? 'cursor-pointer hover:bg-gray-100' : ''
                          }`}
                          onDoubleClick={() => user.equipment[type.id] && handleEquipmentCellDoubleClick(user.id, type.id)}
                        >
                          {user.equipment[type.id] ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              new Date(user.equipment[type.id]) < new Date() 
                                ? 'bg-red-100 text-red-800' 
                                : new Date(user.equipment[type.id]) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {formatDate(user.equipment[type.id])}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {paginatedData.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center space-x-4">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="text-sm pl-4 pr-10 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 max-w-[180px] truncate"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="text-sm text-[#002D43]">
                  Showing {(currentPage - 1) * itemsPerPage + 1}-
                  {Math.min(currentPage * itemsPerPage, currentData.length)} of {currentData.length} items
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {currentPage > 1 && (
                  <button
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 flex items-center justify-center"
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </button>
                )}

                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    className={`px-4 py-2 border ${
                      page === currentPage
                        ? 'border-gray-300 bg-blue-50 text-blue-600'
                        : 'border-gray-300 text-gray-700'
                    } rounded-full hover:bg-gray-50 flex items-center justify-center`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}

                {currentPage < totalPages && (
                  <button
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 flex items-center justify-center"
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      <AssignmentModal
        isOpen={showModal}
        onClose={closeModal}
        mode={modalMode}
        selectedAssignment={selectedAssignment}
        onSave={handleSave}
        users={assignmentData.users}
        safetyEquipment={assignmentData.safetyEquipment}
        assignments={assignmentData.assignments}
      />

      {/* Equipment Details Modal */}
      <EquipmentDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedEquipmentDetails(null);
        }}
        equipmentDetails={selectedEquipmentDetails}
      />

      {/* Return Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReturnConfirmation}
        onClose={() => {
          setShowReturnConfirmation(false);
          setAssignmentToReturn(null);
        }}
        onConfirm={confirmReturn}
        title={assignmentToReturn && assignmentToReturn.length > 1 ? "Return Multiple Equipment" : "Return Equipment"}
        message={
          assignmentToReturn && assignmentToReturn.length > 1 
            ? `Mark ${assignmentToReturn.length} selected equipment items as returned?`
            : "Mark this equipment as returned?"
        }
        confirmText="Return"
        cancelText="Cancel"
        variant="primary"
      />

      {/* Inspection Modal */}
      {selectedEquipmentForInspection && (
        <InspectionModal
          isOpen={showInspectionModal}
          onClose={() => {
            setShowInspectionModal(false);
            setMultiInspectionMode(false);
            setMultiInspectionQueue([]);
            setCurrentInspectionIndex(0);
            setSelectedEquipmentForInspection(null);
          }}
          equipmentId={selectedEquipmentForInspection.id}
          equipmentName={`${selectedEquipmentForInspection.safety_equipment_type_name || selectedEquipmentForInspection.safety_equipment_type?.name || 'Unknown'} - ${selectedEquipmentForInspection.safety_equipment_model_brand || selectedEquipmentForInspection.safety_equipment_brand || ''} ${selectedEquipmentForInspection.safety_equipment_model_name || ''}`}
          equipmentSerialNumber={selectedEquipmentForInspection.safety_equipment_serial_number}
          onSave={handleInspectionSave}
          multiInspectionMode={multiInspectionMode}
          currentInspectionIndex={currentInspectionIndex}
          totalInspections={multiInspectionQueue.length}
          onSkip={handleInspectionSkip}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setAssignmentToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Assignment"
        message="Are you sure you want to delete this assignment? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default EquipmentAssignments;